import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, invoicesService } from '../services/api';
import { InvoiceStatus } from '../types';
import type { InvoiceDto } from '../types';

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'Draft',
  [InvoiceStatus.Approved]: 'Approved',
  [InvoiceStatus.Sent]: 'Sent',
  [InvoiceStatus.Paid]: 'Paid',
  [InvoiceStatus.Overdue]: 'Overdue',
  [InvoiceStatus.Cancelled]: 'Cancelled',
};

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getToken, account } = useAppAuth();

  const [invoice, setInvoice] = useState<InvoiceDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!account || !id) return;
    setLoading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      setInvoice(await invoicesService(api).getById(id));
    } catch (e: any) {
      setError(e.message ?? 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, id]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!id || !confirm('Approving will generate a PDF and email it to the customer. This cannot be undone. Continue?')) return;
    setApproving(true);
    try {
      const api = createApiClient(getToken);
      await invoicesService(api).approve(id);
      await load();
    } catch (e: any) {
      alert(e.message ?? 'Failed to approve invoice.');
    } finally {
      setApproving(false);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    setDownloading(true);
    try {
      const api = createApiClient(getToken);
      const blobUrl = await invoicesService(api).downloadPdf(id);
      window.open(blobUrl, '_blank');
    } catch (e: any) {
      alert(e.message ?? 'Failed to download invoice PDF.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>;
  if (error || !invoice) return <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>⚠️ {error ?? 'Invoice not found'}</div>;

  return (
    <div style={{ width: '100%', padding: '24px 16px' }} data-testid="invoice-detail-page">
      <Link to="/invoices" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Invoices</Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '12px 0 20px' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>{invoice.invoiceNumber}</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            Issued {new Date(invoice.issuedAt).toLocaleDateString('en-GB')}
            {invoice.dueDate && ` · Due ${new Date(invoice.dueDate).toLocaleDateString('en-GB')}`}
          </p>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12, background: '#f3f4f6', color: '#374151' }}>
          {STATUS_LABELS[invoice.status]}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>From</h3>
          <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.providerName}</p>
          {invoice.providerBusinessName && <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.providerBusinessName}</p>}
          <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.providerEmail}</p>
          {invoice.providerPhone && <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.providerPhone}</p>}
          <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.providerAddress}</p>
          {invoice.providerVatNumber && <p style={{ fontSize: 13, margin: '2px 0' }}>VAT: {invoice.providerVatNumber}</p>}
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>To</h3>
          <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.customerName}</p>
          {invoice.customerBusinessName && <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.customerBusinessName}</p>}
          <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.customerEmail}</p>
          {invoice.customerAddress && <p style={{ fontSize: 13, margin: '2px 0' }}>{invoice.customerAddress}</p>}
        </div>
      </div>

      <div style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {invoice.jobReferences.map((ref, i) => (
          <Link key={ref} to={`/jobs/${invoice.jobIds[i]}`} style={{
            fontSize: 12, padding: '3px 8px', borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', textDecoration: 'none',
          }}>{ref}</Link>
        ))}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '6px 4px' }}>Description</th>
            <th style={{ padding: '6px 4px' }}>Qty</th>
            <th style={{ padding: '6px 4px' }}>Unit Price</th>
            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lineItems.map((item, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>{item.description}</td>
              <td style={{ padding: '6px 4px' }}>{item.quantity}</td>
              <td style={{ padding: '6px 4px' }}>£{item.unitPrice.toFixed(2)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>£{item.amount.toFixed(2)}</td>
            </tr>
          ))}
          {invoice.extraItems.map((item, i) => (
            <tr key={`extra-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '6px 4px' }}>{item.description}</td>
              <td style={{ padding: '6px 4px' }}>1</td>
              <td style={{ padding: '6px 4px' }}>£{item.amount.toFixed(2)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>£{item.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <p style={{ fontSize: 13, margin: '2px 0' }}>Subtotal: £{invoice.subtotal.toFixed(2)}</p>
        {invoice.isVatRegistered && <p style={{ fontSize: 13, margin: '2px 0' }}>VAT: £{invoice.vatTotal.toFixed(2)}</p>}
        <p style={{ fontSize: 16, fontWeight: 700, margin: '6px 0 0' }}>Total: £{invoice.total.toFixed(2)}</p>
      </div>

      {invoice.notes && (
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Notes</h3>
          <p style={{ fontSize: 13 }}>{invoice.notes}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
        <button
          data-testid="invoice-download-button"
          onClick={handleDownload}
          disabled={downloading}
          style={{
            fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
            border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer',
          }}
        >
          {downloading ? 'Opening…' : 'Download PDF'}
        </button>
        {invoice.status === InvoiceStatus.Draft && (
          <button
            data-testid="invoice-approve-button"
            onClick={handleApprove}
            disabled={approving}
            style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              background: approving ? '#e5e7eb' : '#2563eb', color: approving ? '#9ca3af' : '#fff',
              border: 'none', cursor: approving ? 'default' : 'pointer',
            }}
          >
            {approving ? 'Approving…' : 'Approve & Send'}
          </button>
        )}
      </div>
      {invoice.status === InvoiceStatus.Draft && (
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          To cancel this draft invoice instead, go back to the <Link to="/invoices">invoice list</Link>.
        </p>
      )}
    </div>
  );
}
